import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      // Skip auth check on public routes to avoid 401 redirect loops
      const publicRoutes = ['/login', '/register', '/reset-password', '/forgot-password'];
      const isPublicRoute = publicRoutes.some(route => 
        window.location.pathname.startsWith(route)
      );
      
      if (isPublicRoute) {
        // On public routes, don't probe - cookie will be set on login
        // Clean up any legacy localStorage data
        localStorage.removeItem('hrms_token');
        localStorage.removeItem('hrms_user');
        setLoading(false);
        return;
      }
      
      // Check session via httpOnly cookie
      try {
        const response = await authAPI.getMe();
        const userData = response.data.user || response.data;
        setUser(userData);
        setRequiresPasswordChange(userData?.requires_password_change || false);
        
        // Clean up legacy localStorage data (httpOnly cookie is now authoritative)
        localStorage.removeItem('hrms_token');
        localStorage.removeItem('hrms_user');
      } catch (err) {
        // Session invalid - clean up any legacy localStorage
        localStorage.removeItem('hrms_token');
        localStorage.removeItem('hrms_user');
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = useCallback(async (credentials) => {
    try {
      setError(null);
      const response = await authAPI.login(credentials);
      const { user: userData, requires_password_change } = response.data;
      
      // Session is managed via httpOnly cookie - no localStorage needed
      setUser(userData);
      setRequiresPasswordChange(requires_password_change || false);
      
      return { user: userData, requires_password_change };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const updateUser = useCallback((newUserData) => {
    setUser(newUserData);
  }, []);

  const clearPasswordChangeRequirement = useCallback(() => {
    setRequiresPasswordChange(false);
    setUser(prev => prev ? { ...prev, requires_password_change: false } : null);
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setError(null);
      const response = await authAPI.register(userData);
      const { user: newUser } = response.data;
      
      // Session is managed via httpOnly cookie
      setUser(newUser);
      
      return newUser;
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie and blacklist token
      await authAPI.logout();
    } catch (err) {
      // Continue with local logout even if server call fails
      console.warn('Logout API call failed:', err);
    }
    // Clean up any legacy localStorage
    localStorage.removeItem('hrms_token');
    localStorage.removeItem('hrms_user');
    setUser(null);
    setRequiresPasswordChange(false);
  }, []);

  const isAdmin = useCallback(() => user?.role === 'admin' || user?.role === 'hr', [user?.role]);
  const isEmployee = useCallback(() => user?.role === 'employee', [user?.role]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAdmin,
    isEmployee,
    isAuthenticated: !!user,
    updateUser,
    requiresPasswordChange,
    clearPasswordChangeRequirement,
  }), [user, loading, error, login, register, logout, isAdmin, isEmployee, updateUser, requiresPasswordChange, clearPasswordChangeRequirement]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
